import React, { useEffect, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { useCredits } from 'customhooks/useCredits';
import { onCreditChange } from 'utils/creditEvents';

interface RealTimeCreditUpdaterProps {
  children: React.ReactNode;
}

/**
 * Listens for `credit-balance-changed` custom events and refreshes
 * the credit balance exactly once per event.  No polling.
 *
 * Any component that consumes credits should call `notifyCreditChange()`
 * after a successful credit-consuming action so this updater picks it up.
 */
const RealTimeCreditUpdater: React.FC<RealTimeCreditUpdaterProps> = ({ children }) => {
  const { refreshCredits } = useCredits();

  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
  const hasToken = !!(
    useSelector((state: RootState) => state.auth.token) ||
    localStorage.getItem('token')
  );

  // Debounce: avoid rapid-fire refreshes if multiple events arrive within 1s
  const lastRefreshRef = useRef<number>(0);
  const pendingRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleCreditChange = useCallback(() => {
    if (!isAuthenticated && !hasToken) return;

    const now = Date.now();
    const elapsed = now - lastRefreshRef.current;

    // If we refreshed less than 1 second ago, debounce
    if (elapsed < 1000) {
      if (pendingRef.current) clearTimeout(pendingRef.current);
      pendingRef.current = setTimeout(() => {
        lastRefreshRef.current = Date.now();
        refreshCredits().catch(() => {});
      }, 1000 - elapsed);
      return;
    }

    lastRefreshRef.current = now;
    refreshCredits().catch(() => {});
  }, [refreshCredits, isAuthenticated, hasToken]);

  useEffect(() => {
    if (!isAuthenticated && !hasToken) return;

    const unsubscribe = onCreditChange(handleCreditChange);
    return () => {
      unsubscribe();
      if (pendingRef.current) clearTimeout(pendingRef.current);
    };
  }, [handleCreditChange, isAuthenticated, hasToken]);

  return <>{children}</>;
};

export default RealTimeCreditUpdater;
